import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";

import COMMON from "common";
import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import validateFileSize from "common/validate-file-size";
import serveRequestErrors from "common/serve-request-errors";
import converReadableFileSize from "common/convert-readable-file-size";
import AppButton from "components/app-button";
import removeIcon from "assets/images/remove-icon.svg";
import { ReactComponent as UploadIcon } from "assets/images/upload-icon.svg";

const AppUploadAdditionalQuotationDocumentsModal = (props, ref) => {
	const dispatch = useDispatch();
	const uploadInputRef = useRef();
	const [isSubmiting, setIsSubmiting] = useState(false);
	const [uploadProgress, setUploadProgress] = useState({});
	const [selectedQuotationId, setSelectedQuotationId] = useState(null);
	const [documents, setDocuments] = useState([]);
	const [visible, setVisible] = useState(false);
	const maxLength = useMemo(() => documents.length >= 5, [documents]);
	const disabled = useMemo(() => !documents.length || isSubmiting, [isSubmiting, documents]);

	const onHandleShow = useCallback((id) => {
		setSelectedQuotationId(id);
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		setDocuments([]);
		setVisible(false);
	}, []);

	//prettier-ignore
	const onHandleRemoveDocument = useCallback((obj) => {
		const filtered = documents.filter((o) => o.name !== obj.name);
		setDocuments(filtered);
	}, [documents]);

	const onHandleUploadDocument = useCallback(() => {
		uploadInputRef.current.click();
	}, []);

	//prettier-ignore
	const onHandleUploadDocumentChange = useCallback((event) => {
        const file = event.target.files[0];

        const files = [...documents];

		const isValidFileSize = validateFileSize(file, COMMON.MAX_FILE_SIZES.MB_15);

		if (!isValidFileSize) return serveRequestErrors(ERRORS.DOCUMENTS_SIZE_15MB);

        uploadInputRef.current.value = "";

		const duplicated = files.findIndex((o) => o.name === file.name) > -1;

		if (!duplicated) files.push(file);

		setDocuments(files);
    }, [documents]);

	//prettier-ignore
	const onHandleMakePayment = useCallback(async (quotationId) => {
		let response = null;

		let ebPolicyRenewalQuotationId = quotationId || selectedQuotationId;

		const payload = {
			ebPolicyRenewalQuotationId,
			ebPolicyRenewalId: props.renewal.id,
			redirectUrl: window.location.href,
		};

		try {
			response = await api.post.policies.renewalPayment(payload);
		} catch (error) {
			serveRequestErrors(error);

			props.onHandleDimissPolicyModal();
		}

		if (response) window.location.href = response.paymentUrl;
	}, [selectedQuotationId, props]);

	//prettier-ignore
	const onHandleUploadPolicyDocument = useCallback(async (index = 0) => {
		const pendingAdditionalDocuments = props.renewal.status === COMMON.STATUS_ID.PENDING_ADDITIONAL_DOCS;

        setIsSubmiting(true);

        let response = null;

        const file = documents[index];

        const onUploadProgress = (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

            setUploadProgress((prev) => ({ ...prev, [file.name]: percentCompleted }));
        };

        try {
            const formData = new FormData();

            const params = props.renewal.id;

			formData.append("file", file);

            await api.post.policies.uploadPolicyRenewal(params, formData, onUploadProgress);

            response = true;
        } catch (error) {
            setIsSubmiting(false);
            serveRequestErrors(error);
        } finally {
            setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
        }

        const nextIndex = index + 1;

        if (response && documents[nextIndex]) {
            onHandleUploadPolicyDocument(nextIndex);
        } else if(response && pendingAdditionalDocuments) {
			onHandleDismiss();
			props.onHandleDimissPolicyModal();
			dispatch(promptAlertMessage({ message: "Documents has been uploaded successfully" }));
		} else if(response) {
			onHandleMakePayment();
        } 
    }, [dispatch, documents, props, onHandleDismiss, onHandleMakePayment]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
		onHandleMakePayment: onHandleMakePayment,
	}));

	return (
		<Modal classes={{ root: "app-upload-additional-quotation-documents-modal" }} open={visible} aria-labelledby="upload-additional-quotation-documents" aria-describedby="upload-additional-quotation-documents-modal">
			<div className="upload-additional-quotation-documents">
				<p className="upload-additional-quotation-documents__title">Upload Document</p>
				<p className="upload-additional-quotation-documents__description">Please ensure that your document is in the correct format (jpg, pdf) with file size not exceeding 15MB.</p>
				<p className="upload-additional-quotation-documents__note">Upload duly signed & stamped renewal invitation & quotation.</p>

				<div className="upload-additional-quotation-documents__body">
					<div className="upload-additional-quotation-documents__list-header">
						<p className="upload-additional-quotation-documents__label">Upload <span className="upload-additional-quotation-documents__asterisk">*</span></p>
						<p className="upload-additional-quotation-documents__label">File Size</p>
						<p className="upload-additional-quotation-documents__label"></p>
					</div>
					{documents.map((a, i) => {
						return (
							<div className="upload-additional-quotation-documents__input" key={i}>
								<p className="upload-additional-quotation-documents__text">{a.name}</p>
								<p className="upload-additional-quotation-documents__text">{converReadableFileSize(a.size, true)}</p>
								<button className="upload-additional-quotation-documents__delete" onClick={() => onHandleRemoveDocument(a)}>
									<img src={removeIcon} alt="remove" />
								</button>

								<div className="upload-additional-quotation-documents__progress-bar" style={{ width: `${uploadProgress[a.name] || 0}%` }} />
							</div>
						);
					})}

					{!maxLength && (
						<div className="upload-additional-quotation-documents__input">
							<button type="button" className="upload-additional-quotation-documents__upload-button" onClick={onHandleUploadDocument}>
								<p className="upload-additional-quotation-documents__upload">Upload</p>
								<UploadIcon />
							</button>
						</div>
					)}

					{/* prettier-ignore */}
					<input type="file" name="file" accept="image/jpeg, image/jpg, image/png, .pdf, .doc, .docx" hidden ref={uploadInputRef} onChange={onHandleUploadDocumentChange} />
				</div>

				<div className="upload-additional-quotation-documents__button-container">
					<AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
					<AppButton type="button" label="Confirm" onClick={() => onHandleUploadPolicyDocument()} disabled={disabled} />
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppUploadAdditionalQuotationDocumentsModal));

AppUploadAdditionalQuotationDocumentsModal.propTypes = {
	ref: PropTypes.object,
};
